import React, { useContext, useState } from "react";
import { X, CreditCard, History } from "lucide-react";
import PlansDisplay from "./PlansDisplay";
import PaymentsTab from "pages/Profile/components/PaymentsTab";
import { ModalsContext } from "components/context/ModalsContext";
import toast from "react-hot-toast";

/**
 * PlansModal - Modal wrapper for subscription plans and payments
 * Shows the same content as the Profile subscription and payments tabs
 */
const PlansModal = ({ 
  isOpen, 
  onClose, 
  title = "Suscripción y Pagos",
  subtitle = "Administra tu plan y revisa tu historial de pagos.",
  onPlanSelect = null // Optional callback when a plan is selected
}) => {
  const { plansModalMessage } = useContext(ModalsContext);
  const [activeTab, setActiveTab] = useState("subscription");

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 modal-safe-area bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 border border-white/20 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/70 hover:text-white transition-all duration-200 p-1.5 rounded-full hover:bg-white/20 backdrop-blur-sm border border-white/10 hover:border-white/30 hover:scale-110 z-10"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="w-full flex flex-col items-start px-8 pt-8 pb-4">
          <div className="flex flex-col gap-3 w-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/40 flex items-center justify-center backdrop-blur-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-bold text-white leading-tight">
                  {title}
                </h2>
                <p className="text-sm text-white/70">
                  {plansModalMessage || subtitle}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="w-full border-t border-white/20" />

        {/* Tabs - Suscripción | Pagos */}
        <div className="px-6 pt-4 border-b border-white/20">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab("subscription")}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition ${
                activeTab === "subscription"
                  ? "bg-white/20 text-white border-b-2 border-blue-400"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              <CreditCard className="w-4 h-4" />
              Suscripción
            </button>
            <button
              onClick={() => setActiveTab("payments")}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition ${
                activeTab === "payments"
                  ? "bg-white/20 text-white border-b-2 border-blue-400"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              <History className="w-4 h-4" />
              Pagos
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6 flex-1 overflow-y-auto">
          {activeTab === "subscription" ? (
            <PlansDisplay
              showCurrentSubscription={true}
              onPlanSelect={onPlanSelect}
              isModal={true}
            />
          ) : (
            <PaymentsTab onError={toast.error} onSuccess={toast.success} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PlansModal; 
